import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { fetchAp2024Branches } from "../api/getApi";
import ShareModal from "../components/shareModal";

const AP2024Branches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "sno", direction: "ascending" });
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareTextVisible, setIsShareTextVisible] = useState(false);

  const currentPageUrl = window.location.href;

  // Effect to show and hide share text
  useEffect(() => {
    const timer1 = setTimeout(() => {
      setIsShareTextVisible(true);
    }, 300);
    const timer = setTimeout(() => {
      setIsShareTextVisible(false);
    }, 2500);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer1);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch branches data
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAp2024Branches();
        
        setBranches(response.data);
      } catch (err) {
        setError("Error fetching data: " + err.message);
        console.error("Error fetching AP branches:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBranches();
  }, []);

  // Handle sorting
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting and filtering
  const sortedAndFilteredBranches = React.useMemo(() => {
    let filteredData = [...branches];
    
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filteredData = filteredData.filter(
        branch =>
          branch.branch_name.toLowerCase().includes(searchTermLower) ||
          branch.branch_code.toLowerCase().includes(searchTermLower)
      );
    }
    
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];

        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === "ascending" ? valA - valB : valB - valA;
        }
        
        const stringValA = String(valA || '').toLowerCase();
        const stringValB = String(valB || '').toLowerCase();

        if (stringValA < stringValB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (stringValA > stringValB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredData;
  }, [branches, searchTerm, sortConfig]);

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    
    return (
      <span className="ml-1">
        {sortConfig.direction === 'ascending' ? (
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </span>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
        <p className="mt-4 text-lg text-gray-700">Loading AP branches data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg max-w-lg text-center">
          <p className="font-bold mb-2">Error Loading Data</p>
          <p>{error}</p>
          <button 
            onClick={() => {
              setLoading(true);
              setError(null);
              window.location.reload();
            }}
            className="mt-4 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <Helmet>
        <title>AP Engineering & Pharmacy Branches | CollegeRank</title>
        <meta name="description" content="Complete list of engineering and pharmacy branches available in Andhra Pradesh for the academic year." />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="mb-4">
          <Link
            to="/"
            className="text-blue-600 hover:underline font-medium flex items-center gap-1"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Home
          </Link>
        </div>
        
        <div className="bg-white shadow-md rounded-lg">
          <div className="p-6 border-b flex justify-between items-start sm:items-center relative">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">AP Engineering & Pharmacy Branches</h1>
              <p className="text-gray-600 mt-1">
                Complete list of {branches.length > 0 ? branches.length : '...'} branches available in Andhra Pradesh colleges for the academic year.
              </p>
            </div>
            <div
              className="relative flex flex-col items-end"
              onMouseEnter={() => setIsShareTextVisible(true)}
              onMouseLeave={() => setIsShareTextVisible(false)}
            >
              <div
                className={`
                  absolute top-[-2.5rem] right-0 bg-blue-300 text-gray-800 px-4 py-2 rounded-lg shadow-lg 
                  text-sm font-medium transition-all duration-300 ease-in-out whitespace-nowrap z-50
                  ${isShareTextVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95"}
                `}
              >
                Share With Friends
              </div>
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="ml-4 mt-2 sm:mt-0 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-3 sm:px-4 rounded-lg flex items-center shadow-sm transition-colors text-sm"
                aria-label="Share this page"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6.001l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path>
                </svg>
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
          </div>

          <div className="p-6 border-b bg-gray-50">
            <div className="max-w-md mx-auto">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search Branches
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by branch name or code..."
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-4 py-2 border"
              />
            </div>
          </div>

          <div className="px-6 py-3 bg-gray-100 border-b">
            <p className="text-sm text-gray-700 text-center">
              Showing <span className="font-medium">{sortedAndFilteredBranches.length}</span> branches
              {searchTerm && (
                <span> matching "<span className="font-medium">{searchTerm}</span>"</span>
              )}
            </p>
          </div>

          <div className="p-6">
            {sortedAndFilteredBranches.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 hidden md:table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-16"
                        onClick={() => requestSort('sno')}
                      >
                        <div className="flex items-center">
                          S.No {getSortDirectionIndicator('sno')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-32"
                        onClick={() => requestSort('branch_code')}
                      >
                        <div className="flex items-center">
                          Branch Code {getSortDirectionIndicator('branch_code')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => requestSort('branch_name')}
                      >
                        <div className="flex items-center">
                          Branch Name {getSortDirectionIndicator('branch_name')}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedAndFilteredBranches.map((branch) => (
                      <tr key={branch._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {branch.sno}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                          {branch.branch_code}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {branch.branch_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="grid grid-cols-1 gap-4 md:hidden">
                  {sortedAndFilteredBranches.map((branch) => (
                    <div 
                      key={branch._id} 
                      className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition"
                    >
                      <div className="flex justify-between items-start">
                        <span className="text-xs text-gray-500">#{branch.sno}</span>
                        <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {branch.branch_code}
                        </span>
                      </div>
                      <h3 className="text-base font-medium text-gray-900 mt-2">
                        {branch.branch_name}
                      </h3>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-500">No branches found matching your search criteria.</p>
                <button 
                  onClick={() => setSearchTerm("")}
                  className="mt-4 text-blue-600 hover:underline"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Understanding Branch Codes</h2>
            <p className="text-gray-600">
              Branch codes are used during the counseling process to select your preferred engineering or pharmacy discipline. 
              These codes are standardized across all colleges in Andhra Pradesh for the academic year.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">How to Use This Information</h2>
            <p className="text-gray-600">
              When filling out your college preferences during counseling, you'll need to specify branch codes. 
              Use this list as a reference to ensure you're selecting the correct branches for your desired field of study.
            </p>
          </div>
        </div>
        
        <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Related Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link 
              to="/ap-2024-colleges" 
              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition"
            >
              <div className="bg-blue-100 p-2 rounded-full mr-3">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">AP Colleges</h3>
                <p className="text-sm text-gray-600">View all colleges in AP</p>
              </div>
            </Link>
            <Link 
              to="/new-branches-2024" 
              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition"
            >
              <div className="bg-green-100 p-2 rounded-full mr-3">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">New Branches</h3>
                <p className="text-sm text-gray-600">Explore branches added</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
      <ShareModal
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        link={currentPageUrl}
      />
    </div>
  );
};

export default AP2024Branches;